import * as React from 'react';
import { Controller } from 'react-hook-form';
import { ModalContainer, Input, LabelButton, Select } from '@teamsnap/snap-ui';

import { convertFromPennies, convertToPennies } from 'core/utils';
import useRecordOfflinePayment, { RecordOfflinePaymentFormValues } from './useRecordOfflinePayment';

type Props = {
  show: boolean;
  onClose: () => void;
  isSubmitting?: boolean;
  onSubmit: (data: RecordOfflinePaymentFormValues) => void;
  balanceDue: number;
  currency: 'USD' | 'CAD';
  currencySymbol: string;
};

const RecordPaymentModal = ({ show, onClose, onSubmit, isSubmitting, balanceDue, currency, currencySymbol }: Props) => {
  const [recordOfflinePaymentForm, submitOfflinePayment, getFormErrors] = useRecordOfflinePayment({
    onSubmit,
  });

  return (
    <ModalContainer
      disableOverlayClose={true}
      data-testid="order-details-actions--record-payment-modal"
      closeButton={false}
      isOpen={show}
      onClose={onClose}
    >
      <div className="sui-mb-2">
        <Controller
          as={
            <Select
              options={['Cash', 'Other']}
              label="Payment type"
              required
              name="paymentType"
              errors={getFormErrors('paymentType')}
            />
          }
          name="paymentType"
          control={recordOfflinePaymentForm.control}
          defaultValue={''}
          disabled={isSubmitting}
          rules={{
            required: 'You must select a payment type',
          }}
        />
      </div>
      <div className="sui-mb-2">
        <Controller
          as={
            <Input
              currency={currency}
              label="Amount"
              name="amount"
              type="financial"
              autocomplete="off"
              errors={getFormErrors('amount')}
              helpText={`You can record the full balance amount or any amount up to ${currencySymbol}${convertFromPennies(balanceDue)}`}
              required
            />
          }
          name="amount"
          control={recordOfflinePaymentForm.control}
          defaultValue={''}
          disabled={isSubmitting}
          rules={{
            required: 'You must enter an amount',
            validate: {
              notGreaterThanBalanceDue: (v) => {
                const pennies = convertToPennies(v);
                return (
                  (+pennies <= +balanceDue && +pennies > 0) || 'You must enter an amount less or equal to the balance owed'
                );
              },
            },
          }}
        />
      </div>
      <div className="sui-mb-2">
        <Controller
          as={<Input label="Note" name="note" type="text" autocomplete="off" />}
          name="note"
          control={recordOfflinePaymentForm.control}
          disabled={isSubmitting}
          defaultValue={''}
        />
      </div>
      <footer className="sui-flex sui-items-center sui-justify-end sui-mt-2">
        <LabelButton
          data-testid="record-offline-payment-cancel"
          labelText="Cancel"
          variantType="secondary"
          onClick={() => onClose()}
          disabled={isSubmitting}
        />
        <LabelButton
          data-testid="record-offline-payment-submit"
          className="sui-ml-1"
          icon="add"
          iconPosition="left"
          labelText="Record offline payment"
          variantType="primary"
          onClick={() => submitOfflinePayment()}
          disabled={isSubmitting}
        />
      </footer>
    </ModalContainer>
  );
};

export default RecordPaymentModal;
